import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import BackgroundDynamic from '@activebrands/core-web/components/BackgroundDynamic';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import Heading from 'components/text/Heading';

// MaxRows 99 beacuse we don't want to limit the number of rows in the feature card but keep the option to change
const FeatureCard = ({
    $style = {},
    background,
    backgroundOverlay,
    heading,
    id,
    maxRows = 99,
    paragraph,
    sbEditable,
}) => {
    const [css] = useStyletron();
    const hasTextContent = !!(heading?.text || paragraph?.text);
    const headingDomId = heading?.text && id ? `feature-card-heading-${id}` : undefined;
    const Root = hasTextContent ? 'article' : 'div';

    return (
        <Root
            className={css({
                position: 'relative',
                ...(hasTextContent ? { ...getFocusRingStyles() } : {}),
                ...$style,
            })}
            aria-labelledby={headingDomId}
            tabIndex={hasTextContent ? 0 : undefined}
            {...sbEditable}
        >
            {background?.length > 0 && (
                <div
                    className={css({
                        aspectRatio: 'var(--ratio-square)',
                        marginBottom: '12px',
                        position: 'relative',
                    })}
                >
                    <BackgroundDynamic
                        $style={{ zIndex: 1 }}
                        background={background}
                        backgroundOverlay={backgroundOverlay}
                        loading="lazy"
                    />
                </div>
            )}
            {(heading?.text || paragraph?.text) && (
                <div
                    className={css({
                        paddingLeft: '4px',
                        paddingRight: ['8px', null, null, null, null, '16px'],
                    })}
                >
                    {heading?.text && (
                        <Heading
                            id={headingDomId}
                            as={heading?.type}
                            fontKeys="Primary/20-120_-1"
                            {...heading?.sbEditable}
                        >
                            {heading?.text}
                        </Heading>
                    )}
                    {paragraph?.text && (
                        <div
                            className={css({
                                display: '-webkit-box',
                                height: 'auto',
                                overflow: 'hidden',
                                marginTop: '8px',
                                '-webkit-box-orient': 'vertical',
                                '-webkit-line-clamp': maxRows,
                                ...textStyles['Secondary/14_130'],
                            })}
                            {...paragraph?.sbEditable}
                        >
                            <RichText data={paragraph?.text} />
                        </div>
                    )}
                </div>
            )}
        </Root>
    );
};

FeatureCard.propTypes = {
    $style: PropTypes.object,
    background: PropTypes.arrayOf(PropTypes.object),
    backgroundOverlay: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    heading: PropTypes.object,
    id: PropTypes.string,
    maxRows: PropTypes.number,
    paragraph: PropTypes.object,
    sbEditable: PropTypes.object,
};

export default FeatureCard;
